"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BookCheck,
  ChevronDown,
  Plus,
  RefreshCcw,
  Users,
} from "lucide-react";
import DashboardOverview from "./components/overview";
import WrongSubmissionsAnalysis from "./components/WrongSubmissionsAnalysis";
import AddStudentModal from "./components/AddStudentModal";
import TeacherProfileModal from "./components/TeacherProfileModal";
import PracticeSessionSetup from "./components/PracticeSessionSetup";
import {
  addStudentToActiveSchool,
  getActiveSchool,
  getTeachersForActiveSchool,
  getTeacherDashboardData,
  repairActiveSchoolRosterIfEmpty,
  waitForInitialSetup,
  type AddStudentPayload,
  type TeacherDashboardData,
  type TeacherStudentOverview,
} from "../../services/backend/api";
import {
  saveTeacherPracticeSession,
  type GradeSectionSelection,
  type SectionName as PracticeSectionName,
  type TeacherPracticeStudent,
} from "../../services/teacherPracticeSession";
import { useProfileContext } from "../../context/ProfileContext";

type Tab = "overview" | "analysis";
type SectionName = "A" | "B" | "C" | "D" | "E";

type DashboardRow = TeacherStudentOverview & {
  wordsSkipped: number;
};

const SECTIONS: SectionName[] = ["A", "B", "C", "D", "E"];
const TEACHER_AVATAR_SRC = "/profile-icons/image-9.png";
const DEFAULT_TEACHER_PASSWORD = "0000";
const TEACHER_PASSWORD_KEY_PREFIX = "teacherPassword:";

const getTeacherPasswordKey = (teacherId: number) =>
  `${TEACHER_PASSWORD_KEY_PREFIX}${teacherId}`;

const isWithinTimeRange = (
  lastPractice: string,
  range: "all" | "month" | "week"
): boolean => {
  if (range === "all") return true;

  const practicedAt = Date.parse(lastPractice);
  if (Number.isNaN(practicedAt)) return false;

  const now = Date.now();
  const days = range === "week" ? 7 : 30;
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  return practicedAt >= cutoff;
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useProfileContext();
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [selectedGrade, setSelectedGrade] = useState<number | "all">("all");
  const [selectedSection, setSelectedSection] = useState<SectionName | "all">(
    "all"
  );
  const [selectedTime, setSelectedTime] = useState<"all" | "month" | "week">(
    "all"
  );
  const [dashboardData, setDashboardData] = useState<TeacherDashboardData>({
    stats: { totalStudents: 0, wordsSpoken: 0, wordsLearned: 0 },
    students: [],
    skipRequests: [],
    wrongSubmissions: [],
  });
  const [schoolName, setSchoolName] = useState("");
  const [teacherName, setTeacherName] = useState("Teacher");
  const [teacherEmail, setTeacherEmail] = useState("teacher@school.com");
  const [activeTeacherId, setActiveTeacherId] = useState<number | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [isPracticeSetupOpen, setIsPracticeSetupOpen] = useState(false);
  const [practiceError, setPracticeError] = useState("");
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [dataStatusMessage, setDataStatusMessage] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState("");

  const loadDashboardData = async (options?: { showRepairMessage?: boolean }) => {
    setIsRefreshingData(true);
    setDataStatusMessage("");

    try {
      await waitForInitialSetup();
      let data = await getTeacherDashboardData();

      let didRepairRoster = false;
      if (data.students.length === 0) {
        didRepairRoster = await repairActiveSchoolRosterIfEmpty();
        if (didRepairRoster) {
          data = await getTeacherDashboardData();
        }
      }

      setDashboardData(data);
      setLastRefreshedAt(new Date().toLocaleTimeString());

      if (data.students.length === 0) {
        setDataStatusMessage(
          "Student list is empty on this device. Tap Refresh Data to retry after setup sync."
        );
      } else if (didRepairRoster && options?.showRepairMessage !== false) {
        setDataStatusMessage("Roster refreshed with the latest school data.");
      }

      const activeSchool = getActiveSchool();
      const teachers = getTeachersForActiveSchool();
      const persistedTeacher = JSON.parse(
        localStorage.getItem("activeTeacher") ?? "null"
      ) as { teacherId?: number; teacherName?: string } | null;
      const activeTeacher =
        teachers.find((teacher) => teacher.teacherId === persistedTeacher?.teacherId) ??
        teachers[0];
      setSchoolName(activeSchool.schoolName);
      setTeacherName(activeTeacher?.teacherName ?? "Teacher");
      setActiveTeacherId(activeTeacher?.teacherId ?? null);

      const generatedEmail = activeTeacher?.teacherName
        ? `${activeTeacher.teacherName.toLowerCase().replace(/[^a-z]/g, "")}@school.com`
        : "teacher@school.com";
      setTeacherEmail(generatedEmail);
    } finally {
      setIsRefreshingData(false);
    }
  };

  useEffect(() => {
    setIsVisible(true);
    void loadDashboardData({ showRepairMessage: false });
  }, []);

  const reloadDashboardData = async () => {
    await loadDashboardData({ showRepairMessage: false });
  };

  const handleUpdatePassword = ({
    currentPassword,
    newPassword,
    confirmPassword,
  }: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): { ok: boolean; message: string } => {
    if (!activeTeacherId) {
      return { ok: false, message: "No active teacher found." };
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      return { ok: false, message: "Please fill in all password fields." };
    }

    const storedPassword =
      localStorage.getItem(getTeacherPasswordKey(activeTeacherId)) ??
      DEFAULT_TEACHER_PASSWORD;

    if (currentPassword !== storedPassword) {
      return { ok: false, message: "Current password is incorrect." };
    }

    if (newPassword.length < 4) {
      return {
        ok: false,
        message: "New password must be at least 4 characters.",
      };
    }

    if (newPassword !== confirmPassword) {
      return { ok: false, message: "New and confirm passwords do not match." };
    }

    localStorage.setItem(getTeacherPasswordKey(activeTeacherId), newPassword);
    return { ok: true, message: "Password updated successfully." };
  };

  const handleTeacherLogout = () => {
    logout();
  };

  const handleAddStudent = async (
    payload: AddStudentPayload
  ): Promise<{ ok: boolean; message: string }> => {
    const result = await addStudentToActiveSchool(payload);
    if (result.ok) {
      await reloadDashboardData();
    }
    return { ok: result.ok, message: result.message };
  };

  const classOptions = Array.from(
    new Set(dashboardData.students.map((student) => student.class))
  ).sort((a, b) => a - b);
  const gradeOptions = classOptions.length > 0 ? classOptions : [1, 2, 3, 4, 5];

  const rows: DashboardRow[] = dashboardData.students.map((student) => ({
    ...student,
    wordsSkipped: student.wordsSkipped,
  }));

  const filteredRows = rows.filter((student) => {
    const byGrade = selectedGrade === "all" || student.class === selectedGrade;
    const bySection = selectedSection === "all" || student.section === selectedSection;
    const byTime = isWithinTimeRange(student.lastPractice, selectedTime);
    return byGrade && bySection && byTime;
  });

  const filteredWrongSubmissions = dashboardData.wrongSubmissions.filter(
    (submission) => {
      const byGrade = selectedGrade === "all" || submission.classId === selectedGrade;
      const bySection =
        selectedSection === "all" || submission.section === selectedSection;
      const byTime = isWithinTimeRange(submission.timestamp, selectedTime);
      return byGrade && bySection && byTime;
    }
  );

  const totalStudents = filteredRows.length;
  const totalWordsLearned = filteredRows.reduce(
    (total, student) => total + student.wordsLearned,
    0
  );
  const totalPronouncedCorrectly = filteredRows.reduce(
    (total, student) => total + student.pronunciationCorrect,
    0
  );
  const totalPronunciationAttempts = filteredRows.reduce(
    (total, student) => total + student.pronunciationAttempted,
    0
  );
  const pronunciationAccuracy =
    totalPronunciationAttempts > 0
      ? Math.round((totalPronouncedCorrectly / totalPronunciationAttempts) * 100)
      : 0;

  const classSectionMap = classOptions.map((grade) => {
    const sections = Array.from(
      new Set(
        rows
          .filter((row) => row.class === grade)
          .map((row) => row.section)
      )
    ).sort();

    return {
      grade,
      sections,
    };
  });

  const buildDefaultPracticeSelection = (): GradeSectionSelection => {
    const defaults = gradeOptions.reduce((acc, grade) => {
      acc[grade] = [];
      return acc;
    }, {} as GradeSectionSelection);

    if (selectedGrade === "all") {
      gradeOptions.forEach((grade) => {
        defaults[grade] =
          selectedSection === "all"
            ? [...SECTIONS]
            : [selectedSection as PracticeSectionName];
      });
      return defaults;
    }

    if (selectedSection === "all") {
      defaults[selectedGrade] = [...SECTIONS];
    } else {
      defaults[selectedGrade] = [selectedSection as PracticeSectionName];
    }

    return defaults;
  };

  const handleStartPractice = (selection: GradeSectionSelection) => {
    const practiceStudents: TeacherPracticeStudent[] = rows
      .filter((student) => (selection[student.class] ?? []).includes(student.section))
      .sort((a, b) => a.class - b.class || a.name.localeCompare(b.name))
      .map((student) => ({
        studentId: student.id,
        studentName: student.name,
        classId: student.class,
        section: student.section,
      }));

    if (practiceStudents.length === 0) {
      setPracticeError(
        "No students found for the selected grades and sections. Please update your selections."
      );
      return;
    }

    saveTeacherPracticeSession({
      fromTeacherDashboard: true,
      selections: selection,
      students: practiceStudents,
      currentStudentId: practiceStudents[0].studentId,
      startedAt: new Date().toISOString(),
    });

    setPracticeError("");
    setIsPracticeSetupOpen(false);
    navigate("/word-prompt", {
      state: { fromTeacherDashboard: true },
    });
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_10%_10%,#ffdbe6_0%,#f3d2ff_50%,#d7c1ff_100%)] p-3 md:p-6">
      <div className="w-full p-1 md:p-2">
        <div
          className={`mb-5 transform transition-all duration-700 ${
            isVisible
              ? "translate-y-0 opacity-100"
              : "-translate-y-10 opacity-0"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight text-[#4b3f76] md:text-4xl">
                Teacher Dashboard
              </h1>
              <p className="mt-1 text-sm font-medium text-[#7f6ea8]">
                {teacherName} • {schoolName}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsProfileOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-[#efddf8] bg-[linear-gradient(180deg,#fff6ff_0%,#fce9ff_100%)] px-3.5 py-2 text-sm font-bold text-[#8d65b7] shadow-md shadow-fuchsia-200/70 transition hover:brightness-105"
            >
              <img
                src={TEACHER_AVATAR_SRC}
                alt="Profile"
                className="h-6 w-6 rounded-full object-cover ring-1 ring-white"
              />
              View Profile
            </button>
          </div>

          <div className="mt-3 flex gap-6 border-b border-[#d8c4eb] text-sm font-semibold md:text-base">
            <button
              onClick={() => setActiveTab("overview")}
              className={`pb-2 transition ${
                activeTab === "overview"
                  ? "border-b-2 border-[#7e57c2] text-[#7e57c2]"
                  : "text-[#8e7ab0] hover:text-[#7e57c2]"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("analysis")}
              className={`pb-2 transition ${
                activeTab === "analysis"
                  ? "border-b-2 border-[#7e57c2] text-[#7e57c2]"
                  : "text-[#8e7ab0] hover:text-[#7e57c2]"
              }`}
            >
              Analysis
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 md:gap-3">
          <div className="relative">
            <select
              value={selectedGrade}
              onChange={(e) =>
                setSelectedGrade(
                  e.target.value === "all" ? "all" : Number(e.target.value)
                )
              }
              className="appearance-none rounded-xl border border-[#d8c4eb] bg-white/85 px-3 py-2 pr-9 text-sm font-medium text-[#6f549f] shadow-sm outline-none transition focus:border-[#7e57c2]"
            >
              <option value="all">Grade</option>
              {classOptions.map((classId) => (
                <option key={classId} value={classId}>
                  Grade {classId}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a84bc]" />
          </div>

          <div className="relative">
            <select
              value={selectedSection}
              onChange={(e) =>
                setSelectedSection(e.target.value as SectionName | "all")
              }
              className="appearance-none rounded-xl border border-[#d8c4eb] bg-white/85 px-3 py-2 pr-9 text-sm font-medium text-[#6f549f] shadow-sm outline-none transition focus:border-[#7e57c2]"
            >
              <option value="all">Section</option>
              {SECTIONS.map((section) => (
                <option key={section} value={section}>
                  {section}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a84bc]" />
          </div>

          <div className="relative">
            <select
              value={selectedTime}
              onChange={(e) =>
                setSelectedTime(e.target.value as "all" | "month" | "week")
              }
              className="appearance-none rounded-xl border border-[#d8c4eb] bg-white/85 px-3 py-2 pr-9 text-sm font-medium text-[#6f549f] shadow-sm outline-none transition focus:border-[#7e57c2]"
            >
              <option value="all">All Time</option>
              <option value="month">This Month</option>
              <option value="week">This Week</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a84bc]" />
          </div>

          <button
            type="button"
            onClick={() => {
              setPracticeError("");
              setIsPracticeSetupOpen(true);
            }}
            className="rounded-xl bg-gradient-to-r from-[#38c172] to-[#2ebd82] px-5 py-2 text-sm font-bold text-white shadow-md shadow-green-200 transition hover:brightness-105"
          >
            Practice
          </button>

          <button
            type="button"
            onClick={() => {
              void loadDashboardData();
            }}
            disabled={isRefreshingData}
            className="inline-flex items-center gap-2 rounded-xl border border-[#d8c4eb] bg-white/85 px-3 py-2 text-sm font-semibold text-[#6f549f] shadow-sm transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <RefreshCcw className={`h-4 w-4 ${isRefreshingData ? "animate-spin" : ""}`} />
            {isRefreshingData ? "Refreshing..." : "Refresh Data"}
          </button>

          {lastRefreshedAt && (
            <span className="text-xs font-medium text-[#8f7bb4]">
              Last refreshed: {lastRefreshedAt}
            </span>
          )}
        </div>

        {practiceError && (
          <div className="mb-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
            {practiceError}
          </div>
        )}

        {dataStatusMessage && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
            <span>{dataStatusMessage}</span>
            <button
              type="button"
              onClick={() => {
                void loadDashboardData();
              }}
              disabled={isRefreshingData}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-400 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 disabled:opacity-60"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${isRefreshingData ? "animate-spin" : ""}`} />
              Retry
            </button>
          </div>
        )}

        <div
          className={`mb-5 grid grid-cols-1 gap-3 md:grid-cols-3 ${
            isVisible ? "animate-fade-in" : ""
          }`}
        >
          <div className="rounded-2xl border border-[#d7c5eb] bg-[#f2ecff] p-4 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#6f8dff] to-[#56b2ff] text-white">
                <Users className="h-5 w-5" />
              </span>
              <button
                type="button"
                onClick={() => setIsAddStudentOpen(true)}
                className="inline-flex items-center gap-1 rounded-lg bg-[#bd97ff] px-2 py-1 text-xs font-semibold text-white shadow"
              >
                <Plus className="h-3.5 w-3.5" />
                Add
              </button>
            </div>
            <p className="text-3xl font-extrabold text-[#523a78]">{totalStudents}</p>
            <p className="text-sm font-medium text-[#7b639e]">Total Students</p>
          </div>

          <div className="rounded-2xl border border-[#e5d9b7] bg-[#fff5d8] p-4 shadow-sm">
            <span className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#ffbc42] to-[#ff9a3d] text-white">
              <BadgeCheck className="h-5 w-5" />
            </span>
            <p className="text-3xl font-extrabold text-[#6e5a28]">
              {pronunciationAccuracy}%
            </p>
            <p className="text-sm font-medium text-[#967b3d]">
              Pronunciation Accuracy Rate
            </p>
          </div>

          <div className="rounded-2xl border border-[#cde7cf] bg-[#e7faec] p-4 shadow-sm">
            <span className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#40c463] to-[#26a865] text-white">
              <BookCheck className="h-5 w-5" />
            </span>
            <p className="text-3xl font-extrabold text-[#2d6e45]">{totalWordsLearned}</p>
            <p className="text-sm font-medium text-[#4f8c66]">Words Learned</p>
          </div>
        </div>

        {activeTab === "overview" ? (
          <DashboardOverview
            studentsData={filteredRows}
            isVisible={isVisible}
          />
        ) : (
          <WrongSubmissionsAnalysis
            isVisible={isVisible}
            submissions={filteredWrongSubmissions}
          />
        )}

        <TeacherProfileModal
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          teacherName={teacherName}
          schoolName={schoolName}
          email={teacherEmail}
          classSectionMap={classSectionMap}
          avatarSrc={TEACHER_AVATAR_SRC}
          onUpdatePassword={handleUpdatePassword}
          onLogout={handleTeacherLogout}
        />

        <AddStudentModal
          isOpen={isAddStudentOpen}
          onClose={() => setIsAddStudentOpen(false)}
          schoolName={schoolName}
          classOptions={classOptions}
          onSubmit={handleAddStudent}
        />

        <PracticeSessionSetup
          isOpen={isPracticeSetupOpen}
          gradeOptions={gradeOptions}
          initialSelection={buildDefaultPracticeSelection()}
          onClose={() => setIsPracticeSetupOpen(false)}
          onStartPractice={handleStartPractice}
        />
      </div>
    </div>
  );
};

export default TeacherDashboard;
